import { Stack } from '@superdispatch/ui';
import { useEffect, useState } from 'react';
import {
  FormikDrawerActions,
  FormikDrawerContent,
} from 'shared/form/FormikDrawer';
import { SuperPayBankAccountDTO } from 'shared/modules/superpay/SuperPayDTO';
import { useBankAccountVerificationAnalytics } from '../../data/SuperPayAnalytics';
import { ChangeBankAccountStepState } from './ChangeBankAccountTypes';
import {
  ICantFindMvCodeLink,
  InstantBankVerificationFormContent,
  useInstantBankVerificationStep,
} from './InstantBankVerificationForm';
import { UploadScreenshotStep } from './UploadScreenshotStep';

type Step = 'code' | 'upload_screenshot';

export function useChangeBankAccountVerifyStep() {
  const [step, setStep] = useState<Step>('code');
  const { trackVerified } = useBankAccountVerificationAnalytics();

  return {
    step,
    showCode: () => setStep('code'),
    showScreenshot: () => setStep('upload_screenshot'),
    trackVerified,
  };
}

interface UseChangeBankAccountCodeFormProps {
  bankAccount?: SuperPayBankAccountDTO;
  onComplete: () => void;
  onLocked: () => void;
  onSwitchToScreenshot: () => void;
}

export function useChangeBankAccountCodeForm({
  bankAccount,
  onComplete,
  onLocked,
  onSwitchToScreenshot,
}: UseChangeBankAccountCodeFormProps) {
  const { trackVerified, trackCantFindMvCode } =
    useBankAccountVerificationAnalytics();
  const { formik, isLocked, isSubmitDisabled } = useInstantBankVerificationStep(
    {
      bankAccount,
      onComplete: () => {
        trackVerified();
        onComplete();
      },
      onLocked,
    },
  );

  return {
    formik,
    isLocked,
    isSubmitDisabled,
    switchToScreenshot: () => {
      trackCantFindMvCode();
      onSwitchToScreenshot();
    },
  };
}

interface ChangeBankAccountVerifyStepProps {
  bankAccount?: SuperPayBankAccountDTO;
  onClose: () => void;
  onForceClose?: () => void;
  onComplete: () => void;
  onStepStateChange: (state: ChangeBankAccountStepState) => void;
}

export function ChangeBankAccountVerifyStep(
  props: ChangeBankAccountVerifyStepProps,
) {
  const { step, showCode, showScreenshot, trackVerified } =
    useChangeBankAccountVerifyStep();

  if (step === 'upload_screenshot') {
    return (
      <UploadScreenshotStep
        onBack={showCode}
        onClose={props.onClose}
        onComplete={props.onComplete}
        onVerified={trackVerified}
        onFormikStateChange={props.onStepStateChange}
      />
    );
  }

  return <CodeStep {...props} onSwitchToScreenshot={showScreenshot} />;
}

interface CodeStepProps extends ChangeBankAccountVerifyStepProps {
  onSwitchToScreenshot: () => void;
}

function CodeStep({
  bankAccount,
  onClose,
  onForceClose,
  onComplete,
  onStepStateChange,
  onSwitchToScreenshot,
}: CodeStepProps) {
  const { formik, isLocked, isSubmitDisabled, switchToScreenshot } =
    useChangeBankAccountCodeForm({
      bankAccount,
      onComplete,
      onLocked: onForceClose ?? onClose,
      onSwitchToScreenshot,
    });
  const isSubmitted = formik.status.type === 'submitted';

  useEffect(() => {
    onStepStateChange({
      isSubmitting: formik.isSubmitting,
      hasUnsavedChanges: formik.dirty && !isSubmitted && !isLocked,
    });
  }, [
    formik.dirty,
    formik.isSubmitting,
    isSubmitted,
    isLocked,
    onStepStateChange,
  ]);

  return (
    <FormikDrawerContent
      formik={formik}
      title="Verify Bank Account"
      onClose={onClose}
      disableNavigationBlock={true}
      disableAutoClose={true}
      actions={
        <FormikDrawerActions
          submitButtonLabel="Verify"
          submitDisabled={isSubmitDisabled}
        />
      }
    >
      <Stack space="small">
        <InstantBankVerificationFormContent bankAccount={bankAccount} />
        <ICantFindMvCodeLink isLocked={isLocked} onClick={switchToScreenshot} />
      </Stack>
    </FormikDrawerContent>
  );
}
